import { useState, useEffect } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import api from "../api/axiosConfig";
import logo from "../assets/logo/xphub-logo.png";

const SILENT_API_CONFIG = {
  silentGlobalError: true,
  skipGlobalErrorToast: true,
};

function normalizeItems(data) {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.results)) return data.results;
  if (Array.isArray(data.items)) return data.items;
  if (Array.isArray(data.games)) return data.games;
  if (Array.isArray(data.data)) return data.data;
  for (const v of Object.values(data)) if (Array.isArray(v)) return v;
  return [];
}

function pickImageUrl(g) {
  return (
    g?.background_image ||
    g?.image ||
    g?.cover ||
    g?.thumbnail ||
    (Array.isArray(g?.short_screenshots) && g.short_screenshots[0]?.image) ||
    null
  );
}

function Auth({
  onAuthSuccess,
  presentation = "page",
  onRequestClose,
  returnTo = "/dashboard",
}) {
  const location = useLocation();
  const navigate = useNavigate();

  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({ username: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [bgImage, setBgImage] = useState("");
  const [mfaState, setMfaState] = useState({
    required: false,
    token: "",
    code: "",
    destination: "",
  });

  const resetMfaState = () =>
    setMfaState({
      required: false,
      token: "",
      code: "",
      destination: "",
    });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setIsLogin(params.get("mode") !== "register");
    setShowPassword(false);
    setError("");
    setSuccess("");
    resetMfaState();
  }, [location.search]);

  useEffect(() => {
    if (presentation === "dialog") return undefined;
    let cancelled = false;

    async function fetchBackgroundImage() {
      try {
        // 1) Tu feed interno
        const res = await api.get("/games/best-of-year", {
          ...SILENT_API_CONFIG,
          params: { limit: 60, page_size: 60 },
        });
        const list = normalizeItems(res.data).map((g) => pickImageUrl(g)).filter(Boolean);

        let url = list.length ? list[Math.floor(Math.random() * list.length)] : null;

        // 2) Fallback a otro feed interno
        if (!url) {
          try {
            const popular = await api.get("/games/top-rated", {
              ...SILENT_API_CONFIG,
              params: { limit: 60, page_size: 60 },
            });
            const l2 = normalizeItems(popular.data).map((g) => pickImageUrl(g)).filter(Boolean);
            if (l2.length) url = l2[Math.floor(Math.random() * l2.length)];
          } catch {
            // ignorar
          }
        }

        // 3) Último recurso: imagen local
        if (!url) url = "/images/FondoHome.jpeg";

        if (!cancelled) setBgImage(url);
      } catch (err) {
        console.error("Error cargando fondo de catálogo:", err?.message);
        if (!cancelled) setBgImage("/images/FondoHome.jpeg");
      }
    }

    fetchBackgroundImage();
    return () => { cancelled = true; };
  }, [presentation]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (mfaState.required && (name === "email" || name === "password")) {
      resetMfaState();
    }
  };

  const validateForm = () => {
    if (!isLogin && formData.username.trim().length < 3) return "Usuario mínimo 3 caracteres";
    if (!formData.email.includes("@")) return "Correo inválido";

    if (isLogin && mfaState.required) {
      if (mfaState.code.trim().length !== 6) {
        return "El código por correo debe tener 6 dígitos";
      }
      return null;
    }

    if (formData.password.length < 8) return "Contraseña mínima de 8 caracteres";
    if (!isLogin && (!/[A-Za-z]/.test(formData.password) || !/\d/.test(formData.password))) {
      return "La contraseña debe incluir al menos una letra y un número";
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    const validationError = validateForm();
    if (validationError) return setError(validationError);

    setLoading(true);

    try {
      if (isLogin) {
        if (mfaState.required) {
          const verifyPayload = {
            mfaToken: mfaState.token,
            code: mfaState.code,
          };
          const verifyRes = await api.post("/auth/mfa/verify-login", verifyPayload);
          setError("");
          setSuccess("");
          resetMfaState();
          onAuthSuccess(verifyRes.data.user);
          navigate(returnTo, { replace: true });
        } else {
          const res = await api.post("/auth/login", {
            email: formData.email,
            password: formData.password,
          });

          if (res.data?.mfaRequired && res.data?.mfaToken) {
            setMfaState((prev) => ({
              ...prev,
              required: true,
              token: res.data.mfaToken,
              code: "",
              destination: res.data.destination || "",
            }));
            setSuccess(res.data?.message || "Te hemos enviado un código por correo.");
            return;
          }

          setError("");
          onAuthSuccess(res.data.user);
          navigate(returnTo, { replace: true });
        }
      } else {
        const registerPayload = {
          username: formData.username,
          email: formData.email,
          password: formData.password,
        };

        const registerRes = await api.post("/auth/register", registerPayload);

        let token = registerRes.data?.token;
        let user = registerRes.data?.user;

        // Fallback defensivo por si algún entorno antiguo aún no devuelve token en /register
        if (!token || !user) {
          const loginRes = await api.post("/auth/login", {
            email: registerPayload.email,
            password: registerPayload.password,
          });
          token = loginRes.data?.token;
          user = loginRes.data?.user;
        }

        if (!user) {
          throw new Error("No se pudo iniciar sesión automáticamente");
        }

        setSuccess("Cuenta creada correctamente. Sesión iniciada.");
        onAuthSuccess(user);
        navigate("/welcome", { replace: true });
      }
    } catch (err) {
      console.error("Error en autenticación:", err.response?.data || err.message);
      setError(err.response?.data?.message || "Error en autenticación");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={presentation === "dialog" ? "auth-dialog" : "auth-container"}
      role={presentation === "dialog" ? "dialog" : undefined}
      aria-modal={presentation === "dialog" ? "true" : undefined}
      aria-labelledby="auth-title"
      data-testid={presentation === "page" ? "auth-page-background" : undefined}
      style={{
        minHeight: presentation === "page" ? "100vh" : undefined,
        position: "relative",
        backgroundImage: presentation === "page" && bgImage ? `url(${bgImage})` : "none",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      {/* Overlay oscuro para legibilidad */}
      {presentation === "page" ? (
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.7)",
            zIndex: 0,
          }}
        />
      ) : null}

      {presentation === "dialog" ? (
        <button
          type="button"
          className="auth-dialog__close"
          aria-label="Cerrar acceso"
          onClick={onRequestClose}
        >
          ×
        </button>
      ) : null}

      <div className="auth-card" style={{ position: "relative", zIndex: 1 }}>
        {presentation === "page" ? (
          <img
            src={logo}
            alt="XPHub Logo"
            style={{
              width: "180px",
              margin: "0 auto 20px",
              display: "block",
            }}
          />
        ) : null}

        <h2 id="auth-title" className="auth-heading">{isLogin ? "Inicia Sesión" : "Crea tu cuenta"}</h2>

        <div className="auth-toggle">
          <button
            onClick={() => {
              setIsLogin(true);
              setShowPassword(false);
              setError("");
              setSuccess("");
              resetMfaState();
            }}
            className={`auth-toggle-btn ${isLogin ? "active" : ""}`}
          >
            Login
          </button>
          <button
            onClick={() => {
              setIsLogin(false);
              setShowPassword(false);
              setError("");
              setSuccess("");
              resetMfaState();
            }}
            className={`auth-toggle-btn ${!isLogin ? "active" : ""}`}
          >
            Registro
          </button>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          {!isLogin && !mfaState.required && (
            <input
              type="text"
              name="username"
              placeholder="Nombre de usuario"
              value={formData.username}
              onChange={handleChange}
              required
              className="auth-input"
            />
          )}
          {!mfaState.required && (
            <>
              <input
                type="email"
                name="email"
                placeholder="Correo electrónico"
                value={formData.email}
                onChange={handleChange}
                required
                className="auth-input"
              />
              <div style={{ position: "relative", marginBottom: 14 }}>
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder="Contraseña"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  autoComplete={isLogin ? "current-password" : "new-password"}
                  className="auth-input"
                  style={{ paddingRight: 48, marginBottom: 0 }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                  title={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                  style={{
                    position: "absolute",
                    top: "50%",
                    right: 10,
                    transform: "translateY(-50%)",
                    zIndex: 2,
                    width: 32,
                    height: 32,
                    border: "1px solid rgba(255,255,255,0.12)",
                    borderRadius: 8,
                    background: "rgba(255,255,255,0.04)",
                    color: "#dce8f7",
                    display: "grid",
                    placeItems: "center",
                    cursor: "pointer",
                  }}
                >
                  <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M2.25 12s3.5-6 9.75-6 9.75 6 9.75 6-3.5 6-9.75 6-9.75-6-9.75-6Z"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <circle cx="12" cy="12" r="2.75" stroke="currentColor" strokeWidth="1.8" />
                    {!showPassword && (
                      <path
                        d="M4.5 19.5 19.5 4.5"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                      />
                    )}
                  </svg>
                </button>
              </div>
            </>
          )}

          {isLogin && !mfaState.required && (
            <div style={{ textAlign: "right", marginBottom: "10px" }}>
              <Link to="/forgot-password" className="auth-link">
                ¿Olvidaste tu contraseña?
              </Link>
            </div>
          )}

          {isLogin && mfaState.required && (
            <div
              style={{
                border: "1px solid rgba(255,255,255,0.15)",
                borderRadius: 10,
                padding: 12,
                marginBottom: 10,
              }}
            >
              <p style={{ marginTop: 0, marginBottom: 10, color: "#ddd", fontSize: 14 }}>
                Introduce el código de 6 dígitos enviado a{" "}
                <strong>{mfaState.destination || formData.email}</strong>.
              </p>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                placeholder="Código por correo (6 dígitos)"
                value={mfaState.code}
                onChange={(e) =>
                  setMfaState((prev) => ({
                    ...prev,
                    code: e.target.value.replace(/\D+/g, "").slice(0, 6),
                  }))
                }
                required
                className="auth-input"
              />

              <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                <button
                  type="button"
                  className="auth-toggle-btn"
                  onClick={resetMfaState}
                  style={{ width: "100%" }}
                >
                  Volver
                </button>
              </div>
            </div>
          )}

          {error && <p className="auth-error">{error}</p>}
          {success && <p style={{ color: "#39d98a", fontSize: "14px" }}>{success}</p>}

          <button type="submit" className="auth-submit-btn" disabled={loading}>
            {loading ? "Procesando..." : isLogin ? (mfaState.required ? "Verificar código" : "Entrar") : "Registrarse"}
          </button>

          {presentation === "page" ? (
            <div className="auth-back">
              <Link to="/" className="auth-link">← Volver al inicio</Link>
            </div>
          ) : null}
        </form>
      </div>
    </div>
  );
}

export default Auth;
